import type { Fraction } from '@mimir/common/util/fractionType';
import { subtractFractions } from '@mimir/common/util/integerMath';


/**
 * This is the common ancestor for all UI individual timeline elements.
 */
interface UiBox {

  /**
   * A unique ID that is safe to use as key when rendering a timeline
   * box as a vue component.
   */
  boxId: string;

  /**
   * Indicates if the box is currently being dragged.
   */
  dragged: boolean;

  /**
   * Start position of the box in seconds from the beginning of the timeline.
   */
  boxStartPositionInSeconds: Fraction;

}


/**
 * This is the common ancestor for boxes that represent parts
 * of the media of a Mimir item.
 */
interface UiMimirItemBox extends UiBox {

  /**
   * The Mimir item ID.
   */
  itemId: string;

}


/**
 * A box that represents some video-only content of a Mimir item.
 */
export interface UiVideoBox extends UiMimirItemBox {
  type: 'video';

  /**
   * The portion of the Mimir item that this box represents.
   */
  inPointInSeconds: Fraction;
  outPointInSeconds: Fraction;
}


/**
 * A box that represents some content from a specific audio channel
 * of a Mimir item.
 */
export interface UiAudioBox extends UiMimirItemBox {
  type: 'audio';

  /**
   * The portion of the Mimir item that this box represents.
   */
  inPointInSeconds: Fraction;
  outPointInSeconds: Fraction;

  /**
   * The index of the item's channel that this box represents.
   */
  channel: number;
}


/**
 * A box that represents an image from a Mimir item.
 */
export interface UiImageBox extends UiMimirItemBox {
  type: 'image';

  /**
   * An interval that represents how long the image should
   * be displayed.
   */
  inPointInSeconds: Fraction;
  outPointInSeconds: Fraction;
}


export type UiVideoTrackBox = UiVideoBox | UiImageBox;
export type UiAudioTrackBox = UiAudioBox;

export type UiVideoTrack = UiVideoTrackBox[];
export type UiAudioTrack = UiAudioBox[];


export type UiTrackBox = UiVideoTrackBox | UiAudioBox;

/**
 * This interface describes the public state that the timeline
 * UI can render.
 */
export interface TimelineUIState {
  canUndo: boolean;
  canRedo: boolean;
  videoTracks: UiVideoTrack[];
  audioTracks: UiAudioTrack[];
}


export function getBlankTimelineUIState(): TimelineUIState {
  return {
    canUndo: false,
    canRedo: false,
    videoTracks: [],
    audioTracks: [],
  };
}


export function getTrackBoxDuration(trackBox: UiTrackBox): Fraction {
  switch (trackBox.type) {
    case 'video':
    case 'audio':
    case 'image': return subtractFractions(trackBox.outPointInSeconds, trackBox.inPointInSeconds);
  }
}


export function getTrackBoxInPoint(trackBox: UiTrackBox): Fraction {
  switch (trackBox.type) {
    case 'video':
    case 'audio':
    case 'image': return trackBox.inPointInSeconds;
  }
}


export function getTrackBoxOutPoint(trackBox: UiTrackBox): Fraction {
  switch (trackBox.type) {
    case 'video':
    case 'audio':
    case 'image': return trackBox.outPointInSeconds;
  }
}


export function isVideoTrackBox(trackBox: UiTrackBox): trackBox is UiVideoTrackBox {
  switch (trackBox.type) {
    case 'video':
    case 'image': return true;
    case 'audio': return false;
  }
}
